import { DashboardWidgetDataState } from '../widget/dashboard-widget-handle';
import { LazyComponentState } from './lazy-component';

export const DEFAULT_EMPTY_MESSAGE = 'Dashboard.Frame.Empty';
export const DEFAULT_DATA_ERROR_MESSAGE = 'Dashboard.Frame.DataError';
export const CODE_ERROR_MESSAGE = 'Dashboard.Frame.CodeError';
export const LOADING_SPINNER_MIN_ROWS = 3;

export type WidgetFrameView =
  | { kind: 'unavailable' }
  | { kind: 'code-loading' }
  | { kind: 'code-failed'; message: string }
  /** Nothing shown yet: an indicator stands in for the body */
  | { kind: 'first-load' }
  /** The body stays visible and shows its own indicator */
  | { kind: 'refreshing' }
  | { kind: 'ready' }
  | { kind: 'empty'; message: string }
  | { kind: 'failed'; message: string; retry?: () => void };

export interface WidgetFrameInput {
  registered: boolean;
  code: LazyComponentState['status'];
  data: DashboardWidgetDataState;
  hasShownData: boolean;
}

export function widgetFrameView({ registered, code, data, hasShownData }: WidgetFrameInput): WidgetFrameView {
  if (!registered) {
    return { kind: 'unavailable' };
  }
  if (code === 'loading') {
    return { kind: 'code-loading' };
  }
  if (code === 'failed') {
    return { kind: 'code-failed', message: CODE_ERROR_MESSAGE };
  }
  switch (data.status) {
    case 'loading':
      return hasShownData ? { kind: 'refreshing' } : { kind: 'first-load' };
    case 'empty':
      return { kind: 'empty', message: data.message ?? DEFAULT_EMPTY_MESSAGE };
    case 'failed':
      return { kind: 'failed', message: data.message ?? DEFAULT_DATA_ERROR_MESSAGE, retry: data.retry };
    case 'ready':
      return { kind: 'ready' };
  }
}

/** The body is rendered under every data state, so it keeps reporting; these hide it */
export function hidesBody(view: WidgetFrameView): boolean {
  return view.kind === 'first-load' || view.kind === 'empty' || view.kind === 'failed';
}

export function showsBody(view: WidgetFrameView): boolean {
  return view.kind === 'ready' || view.kind === 'refreshing';
}

export function loadingIndicatorFor(rows: number): 'spinner' | 'bar' {
  return rows >= LOADING_SPINNER_MIN_ROWS ? 'spinner' : 'bar';
}
